import LayerGroup from 'ol/layer/Group';
import BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import UrlTile from 'ol/source/UrlTile';
import TileState from 'ol/TileState';
import { BaseLayerHealthSettings } from './BaseLayerHealthSettings';
import Tile from 'ol/Tile';
import { addTileFailureListener, cancelTileLoad } from './TileFetchLoader';

/**
 * Watches the visible base layer for tile failures (see TileFetchLoader) and, when
 * it looks unavailable, shows a toast and switches to the next fallback layer.
 *
 * The fallback is applied by changing layer visibility and the radio selection
 * only: it never goes through MapViewStore, so the user's saved choice is kept.
 * Picking a layer by hand cancels the fallback state for that layer.
 */
export class BaseLayerHealthMonitor {

    private _currentName: string = null;
    private _consecutiveTimeouts = 0;
    private _failedNames: string[] = [];
    //layers holding tiles that were cancelled or refused, which must be reloaded when shown again
    private _dirtyNames: string[] = [];
    private _switching = false;
    private _toastTimer: number = null;
    private _toast: JQuery;
    //which layer each loading tile belongs to (the load function doesn't know)
    private _tileLayerNames = new WeakMap<Tile, string>();

    /** @param mapElement the map's target element (position: relative), which hosts the toast */
    public constructor(private _layerGroup: LayerGroup, mapElement: HTMLElement) {
        this._toast = $("<div class='basemap-toast' role='status' aria-live='polite'></div>")
            .hide()
            .appendTo(mapElement);

        addTileFailureListener((tile) => {
            const name = this._tileLayerNames.get(tile);
            if (name !== undefined) {
                this.markDirty(name);
                if (name === this._currentName) {
                    this.onTileFailed(name);
                }
            }
        });

        this._layerGroup.getLayers().forEach((layer) => {
            this.watchLayer(layer);
            if (layer.getVisible()) {
                this._currentName = layer.get("name");
            }
        });
        console.debug(`Basemap health monitor watching "${this._currentName}"`);
    }

    private watchLayer = (layer: BaseLayer): void => {
        const name: string = layer.get("name");

        //tiles of this layer currently loading, so they can be cancelled when it is hidden
        const loading = new Set<Tile>();

        layer.on("change:visible", () => {
            if (layer.getVisible()) {
                this.onLayerShown(name);
            }
            else {
                //the user (or a fallback) moved on: don't leave this layer's requests holding
                //queue slots (16 map-wide) that the newly shown layer needs
                const pending: Tile[] = [];
                loading.forEach((tile) => pending.push(tile));
                pending.forEach((tile) => {
                    this.markDirty(name);
                    cancelTileLoad(tile);
                });
            }
        });

        const source = (layer as TileLayer).getSource() as UrlTile;
        if (!source) {
            return;
        }

        //OpenLayers can still start tiles for a layer that has just been hidden (they were
        //queued from an earlier frame). End those at once so they don't hold queue slots
        //(16 map-wide) that the newly shown layer needs.
        const loadTile = source.getTileLoadFunction();
        source.setTileLoadFunction((tile: Tile, src: string) => {
            if (layer.getVisible()) {
                loadTile(tile, src);
            }
            else {
                this.markDirty(name);
                tile.setState(TileState.ERROR);
            }
        });

        source.on("tileloaderror", (e: any) => {
            loading.delete(e.tile);
        });

        source.on("tileloadend", (e: any) => {
            loading.delete(e.tile);
            if (name === this._currentName) {
                this._consecutiveTimeouts = 0;
            }
        });

        source.on("tileloadstart", (e: any) => {
            loading.add(e.tile);
            this._tileLayerNames.set(e.tile, name);
        });
    }

    private markDirty = (name: string): void => {
        if (this._dirtyNames.indexOf(name) < 0) {
            this._dirtyNames.push(name);
        }
    }

    private onLayerShown = (name: string): void => {
        this._currentName = name;
        this._consecutiveTimeouts = 0;

        if (this._dirtyNames.indexOf(name) >= 0) {
            //cancelled/refused tiles are cached in the error state: clear them so this layer retries
            this._dirtyNames = this._dirtyNames.filter((n) => n !== name);
            this._layerGroup.getLayers().forEach((layer) => {
                if (layer.get("name") === name) {
                    ((layer as TileLayer).getSource() as UrlTile).refresh();
                }
            });
        }

        if (!this._switching) {
            //chosen by the user (or the Bing zoom swap): forget earlier failures so every
            //layer, including the fallbacks, is tried again from scratch
            this._failedNames = [];
            this.hideToast();
        }
    }

    private onTileFailed = (name: string): void => {
        this._consecutiveTimeouts += 1;

        if (this._consecutiveTimeouts >= BaseLayerHealthSettings.failureThreshold) {
            this._consecutiveTimeouts = 0;
            this.onLayerUnavailable(name);
        }
    }

    private onLayerUnavailable = (name: string): void => {
        if (this._failedNames.indexOf(name) < 0) {
            this._failedNames.push(name);
        }
        console.warn(`Basemap "${name}" looks unavailable (${BaseLayerHealthSettings.failureThreshold} consecutive tile failures)`);

        const next = this.findFallback();
        if (next === null) {
            this.showToast("Base maps are currently unavailable.");
            return;
        }

        console.warn(`Basemap fallback: switching to "${next}"`);
        this.showToast("The currently selected basemap seems to be unavailable at this time. Switching to a new basemap");
        this.switchTo(next);
    }

    private findFallback = (): string => {
        const candidates = BaseLayerHealthSettings.fallbackLayerNames;
        for (let i = 0; i < candidates.length; i++) {
            if (this._failedNames.indexOf(candidates[i]) < 0 && this.hasLayer(candidates[i])) {
                return candidates[i];
            }
        }
        return null;
    }

    private hasLayer = (name: string): boolean => {
        let found = false;
        this._layerGroup.getLayers().forEach((layer) => {
            if (layer.get("name") === name) {
                found = true;
            }
        });
        return found;
    }

    private switchTo = (name: string): void => {
        this._switching = true;
        try {
            this._layerGroup.getLayers().forEach((layer) => {
                layer.setVisible(layer.get("name") === name);
            });
            $("input[name='baseLayer']").each(function () {
                $(this).prop("checked", $(this).val() === name);
            });
        }
        finally {
            this._switching = false;
        }
    }

    private showToast = (message: string): void => {
        this._toast.text(message).show();

        if (this._toastTimer !== null) {
            window.clearTimeout(this._toastTimer);
        }
        this._toastTimer = window.setTimeout(this.hideToast, BaseLayerHealthSettings.toastDurationMs);
    }

    private hideToast = (): void => {
        if (this._toastTimer !== null) {
            window.clearTimeout(this._toastTimer);
            this._toastTimer = null;
        }
        this._toast.hide();
    }
}
